import { useEffect, useMemo, useState } from 'react';
import {
    TechnicalVisitAnswer,
    TechnicalVisitFormDefinition,
    TechnicalVisitQuestion
} from '../types/technicalVisit.types';
import {
    buildTechnicalVisitPayloadResponses,
    getDefaultAnswerId,
    getTechnicalVisitQuestions,
    parseTechnicalVisitResponse
} from '../utils/technicalVisitUtils';

const EMPTY_INITIAL_RESPONSES: any[] = [];

export const useTechnicalVisitForm = (
    formDefinition: TechnicalVisitFormDefinition,
    initialResponses: any[] = EMPTY_INITIAL_RESPONSES
) => {
    const [selectedAnswers, setSelectedAnswers] = useState<Record<number, number>>({});
    const [selectedRecomendaciones, setSelectedRecomendaciones] = useState<
        Record<number, number[]>
    >({});

    const questions = useMemo(() => getTechnicalVisitQuestions(formDefinition), [formDefinition]);
    const normalizeDescription = (value?: string) => value?.trim().toUpperCase();
    const getInitialResponseForQuestion = (question: TechnicalVisitQuestion) =>
        initialResponses.find((response: any) => {
            const responseSectionId = Number(response?.idSeccion ?? response?.seccion?.id);
            return responseSectionId === question.id;
        });

    useEffect(() => {
        const initialAnswers = questions.reduce<Record<number, number>>((answers, question) => {
            const parsedResponse = parseTechnicalVisitResponse(
                getInitialResponseForQuestion(question),
                question
            );

            const defaultAnswerId =
                parsedResponse.answerId ||
                question.tiposRespuesta.find(
                    (answer) =>
                        parsedResponse.answerDescription &&
                        normalizeDescription(answer.descripcion) ===
                            normalizeDescription(parsedResponse.answerDescription)
                )?.id ||
                question.tiposRespuesta.find((answer) => answer.selected)?.id ||
                getDefaultAnswerId(question);

            return {
                ...answers,
                [question.id]: defaultAnswerId
            };
        }, {});

        const initialSelectedRecomendaciones = questions.reduce<Record<number, number[]>>(
            (current, question) => {
                const parsedResponse = parseTechnicalVisitResponse(
                    getInitialResponseForQuestion(question),
                    question
                );

                if (!parsedResponse.recomendacionesSeleccionadas?.length) {
                    return current;
                }

                return {
                    ...current,
                    [question.id]: parsedResponse.recomendacionesSeleccionadas
                };
            },
            {}
        );

        setSelectedAnswers(initialAnswers);
        setSelectedRecomendaciones(initialSelectedRecomendaciones);
    }, [initialResponses, questions]);

    const handleAnswerChange = (question: TechnicalVisitQuestion, answer: TechnicalVisitAnswer) => {
        setSelectedAnswers((current) => ({
            ...current,
            [question.id]: answer.id
        }));

        if (!answer.requiereRecomendacion) {
            setSelectedRecomendaciones((current) => ({
                ...current,
                [question.id]: []
            }));
        }
    };

    const handleRecomendacionToggle = (
        questionId: number,
        recomendacionId: number,
        checked: boolean
    ) => {
        setSelectedRecomendaciones((current) => {
            const currentIds = current[questionId] ?? [];
            const nextIds = checked
                ? [...currentIds, recomendacionId]
                : currentIds.filter((id) => id !== recomendacionId);

            return {
                ...current,
                [questionId]: nextIds
            };
        });
    };

    const getSelectedAnswer = (question: TechnicalVisitQuestion) =>
        question.tiposRespuesta.find((answer) => answer.id === selectedAnswers[question.id]);

    const getAnswerByDescription = (question: TechnicalVisitQuestion, description: string) =>
        question.tiposRespuesta.find(
            (answer) =>
                normalizeDescription(answer.descripcion) === normalizeDescription(description)
        );

    const hasSelectedAnswers = questions.every((question) => Boolean(selectedAnswers[question.id]));

    const hasSeccionesIncumplidasSinRecomendacion = questions.some((question) => {
        const selectedAnswer = getSelectedAnswer(question);

        return (
            Boolean(selectedAnswer?.requiereRecomendacion) &&
            !(selectedRecomendaciones[question.id]?.length)
        );
    });

    const payloadResponses = useMemo(
        () =>
            buildTechnicalVisitPayloadResponses({
                formDefinition,
                selectedAnswers,
                selectedRecomendaciones
            }),
        [formDefinition, selectedRecomendaciones, selectedAnswers]
    );

    return {
        questions,
        selectedAnswers,
        selectedRecomendaciones,
        payloadResponses,
        isValid: hasSelectedAnswers,
        hasSeccionesIncumplidasSinRecomendacion,
        handleAnswerChange,
        handleRecomendacionToggle,
        getSelectedAnswer,
        getAnswerByDescription
    };
};
